import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { HandCoinsIcon as HandCoins, CheckCircleIcon as CheckCircle, XIcon as X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError, formatDate } from "@/lib/utils-app";
import EmptyState from "@/components/EmptyState";
import { RowListSkeleton } from "@/components/SkeletonList";

const STATUS_CLS = {
  pending: "bg-yellow-50 border-yellow-200 text-yellow-800",
  accepted: "bg-green-50 border-green-200 text-green-800",
  declined: "bg-zinc-50 border-zinc-200 text-zinc-800",
};

export default function OffersInbox() {
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/offers/my");
      setSent(r.data?.sent || []);
      setReceived(r.data?.received || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const accept = async (id) => {
    try {
      await api.post(`/offers/${id}/accept`);
      toast.success("Offer accepted — buyer notified");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const decline = async (id) => {
    const reason = prompt("Reason (optional)?") || "";
    try {
      await api.post(`/offers/${id}/decline`, { reason });
      toast.success("Offer declined");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Marketplace</p>
        <h1 className="font-spectral text-4xl tracking-tight flex items-center gap-2"><HandCoins size={28} weight="duotone" /> Offers</h1>
      </div>

      {loading ? <RowListSkeleton count={4} testId="offers-loading-skeleton" /> : (
        <div className="grid lg:grid-cols-2 gap-8">
          <section data-testid="offers-received-section">
            <h2 className="font-spectral text-xl mb-3">Received <span className="text-base text-[#6B6B6B] font-manrope">({received.length})</span></h2>
            {received.length === 0 ? (
              <EmptyState
                testId="offers-received-empty"
                icon={<HandCoins size={28} weight="duotone" />}
                title="No offers received yet"
                description="When buyers make an offer on one of your listed dogs, it'll show up here for you to accept or decline."
                ctaLabel="Manage listings"
                ctaTo="/dogs"
              />
            ) : received.map((o) => (
              <div key={o.id} className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-3" data-testid={`offer-received-${o.id}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
                  <h3 className="font-medium"><Link to={`/dogs/${o.dog_id}`} className="hover:text-crimson">{o.dog_name}</Link></h3>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_CLS[o.status]}`}>{o.status}</span>
                </div>
                <div className="text-sm text-[#6B6B6B]">From {o.buyer_name || "Buyer"} · {formatDate(o.created_at)}</div>
                <div className="font-spectral text-2xl mt-1">${o.amount.toFixed(2)}</div>
                {o.message && <p className="text-sm text-ink mt-2 bg-[#FAFAFA] border border-[#E5E5E5] rounded p-2 italic">"{o.message}"</p>}
                {o.status === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={() => accept(o.id)} className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white" data-testid={`offer-accept-${o.id}`}>
                      <CheckCircle size={12} className="mr-1" weight="fill" /> Accept & enable Buy Now
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decline(o.id)} className="rounded-full border-[#E5E5E5]" data-testid={`offer-decline-${o.id}`}>
                      <X size={12} className="mr-1" /> Decline
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </section>
          <section data-testid="offers-sent-section">
            <h2 className="font-spectral text-xl mb-3">Sent <span className="text-base text-[#6B6B6B] font-manrope">({sent.length})</span></h2>
            {sent.length === 0 ? (
              <EmptyState
                testId="offers-sent-empty"
                icon={<HandCoins size={28} weight="duotone" />}
                title="No offers sent"
                description="Browse the marketplace and use 'Make an offer' on any listing to negotiate with the seller."
                ctaLabel="Browse marketplace"
                ctaTo="/marketplace"
              />
            ) : sent.map((o) => (
              <div key={o.id} className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-3" data-testid={`offer-sent-${o.id}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
                  <h3 className="font-medium"><Link to={`/dogs/${o.dog_id}`} className="hover:text-crimson">{o.dog_name}</Link></h3>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_CLS[o.status]}`}>{o.status}</span>
                </div>
                <div className="text-sm text-[#6B6B6B]">{formatDate(o.created_at)}</div>
                <div className="font-spectral text-2xl mt-1">${o.amount.toFixed(2)}</div>
                {o.status === "accepted" && (
                  <Button asChild size="sm" className="rounded-full bg-[#C9A227] hover:bg-[#0E0E0E] text-white mt-3">
                    <Link to={`/marketplace?focus=${o.dog_id}`} data-testid={`offer-checkout-${o.id}`}>Buy now at $${o.amount.toFixed(2)}</Link>
                  </Button>
                )}
              </div>
            ))}
          </section>
        </div>
      )}
    </AppShell>
  );
}
